import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarPlus, Plus, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ButtonMain from "@/components/buttons/button_main";

// Listers publish their schedule so they get five slots; renters send a
// short wish list of up to three preferred times (the backend enforces the
// same cap with a 422). Same modal, same endpoint, role only changes copy
// and the cap.
const MAX_SLOTS_BY_ROLE = { lister: 5, renter: 3 };

const COPY = {
  lister: {
    title: "Propose a viewing",
    subtitle: "Offer up to five times. The renter accepts one.",
    submit: "Send viewing times",
    success: "Viewing times sent. The renter picks one and you both get it confirmed.",
  },
  renter: {
    title: "Request a viewing",
    subtitle: "Suggest up to three times that work for you. The lister confirms one.",
    submit: "Send viewing request",
    success: "Viewing request sent. The lister confirms one of your times.",
  },
};

// Shared propose modal: pick date and time slots and send them to the other
// party as a viewing proposal. role is "lister" (default) or "renter".
export default function ProposeViewingModal({ open, matchId, onClose, onProposed, role = "lister" }) {
  const [slots, setSlots] = useState([""]);
  const [sending, setSending] = useState(false);
  const maxSlots = MAX_SLOTS_BY_ROLE[role] || MAX_SLOTS_BY_ROLE.lister;
  const copy = COPY[role] || COPY.lister;

  function setSlot(i, v) {
    setSlots((s) => s.map((x, idx) => (idx === i ? v : x)));
  }

  async function submit() {
    const iso = slots
      .filter(Boolean)
      .map((v) => new Date(v).toISOString())
      .slice(0, maxSlots);
    if (!iso.length) {
      toast.warning("Pick at least one date and time.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/matches/${matchId}/viewings`, { slots: iso });
      onProposed?.(res.data?.viewing || res.data || { id: `vw_${Date.now()}`, match_id: matchId, slots: iso, accepted_slot: null, status: "proposed" });
      toast.success(copy.success);
      setSlots([""]);
      onClose?.();
    } catch (error) {
      if (!error.response) {
        // Offline browsing — show the proposed state locally.
        onProposed?.({ id: `vw_demo_${Date.now()}`, match_id: matchId, slots: iso, accepted_slot: null, status: "proposed", demo: true });
        toast.success(copy.success);
        setSlots([""]);
        onClose?.();
      } else {
        toast.warning("We could not send those times. Please try again.");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 32, opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-3xl bg-night-surface p-6 shadow-ambient sm:rounded-3xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-night-tint text-brand-primary">
                  <CalendarPlus size={18} />
                </span>
                <div>
                  <h2 className="font-display text-xl font-semibold text-night-text">{copy.title}</h2>
                  <p className="text-xs text-night-sub">{copy.subtitle}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded-full p-2 text-night-sub transition hover:bg-night-tint hover:text-night-text"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 space-y-2.5">
              {slots.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={v}
                    onChange={(e) => setSlot(i, e.target.value)}
                    className="min-w-0 flex-1 rounded-xl border border-night-line bg-night px-3.5 py-2.5 text-sm text-night-text outline-none transition focus:border-brand-primary"
                  />
                  {slots.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove slot ${i + 1}`}
                      onClick={() => setSlots((s) => s.filter((_, idx) => idx !== i))}
                      className="rounded-full p-2 text-night-sub transition hover:bg-night-tint hover:text-night-text"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {slots.length < maxSlots && (
              <button
                type="button"
                onClick={() => setSlots((s) => [...s, ""])}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary transition hover:text-brand-primary-hover"
              >
                <Plus size={14} /> Add another time
              </button>
            )}

            <ButtonMain full className="mt-5" onClick={submit} disabled={sending}>
              {sending ? (
                <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Sending</span>
              ) : (
                copy.submit
              )}
            </ButtonMain>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
